// View model for the Snapshot route. Every number is point-in-time as of the
// selected run's data date; `prev` is the same measure from the previous
// successful run. `null` means "no data source" and renders as an em dash
// (never a fabricated zero); `prev: null` suppresses the delta chip.
import { RunStatus } from "../../services/appsync/types";

export type SnapshotUnit = "$" | "u" | "%" | "w";

/** Scopes FLOW figures only. Stocks and rates are point-in-time as of the
 *  run's data date and are byte-identical between periods. */
export type SnapshotPeriod = "week" | "month";

export const PERIOD_LABEL: Record<SnapshotPeriod, string> = {
  week: "wk",
  month: "mo",
};

/** Supply unit follows the period: weeks of supply / months of supply. */
export const SUPPLY_UNIT_LABEL: Record<SnapshotPeriod, string> = {
  week: "w",
  month: "mo",
};

export const SUPPLY_ABBREV: Record<SnapshotPeriod, string> = {
  week: "WOS",
  month: "MOS",
};

export const WEEKS_PER_MONTH = 52 / 12;

export type SnapshotChannel = {
  channel: string;
  units: number;
  revenue: number | null;
  /** Share of the period's total outflow, 0..1. */
  share: number;
  /** True for the rolled-up remainder, which is not a real channel. */
  isOther: boolean;
};

export type SnapshotChannelMixEntry = {
  channel: string;
  units: number;
  /** Share of THIS location's outflow, 0..1. */
  share: number;
};

export type SnapshotMetric = {
  current: number | null;
  prev: number | null;
  unit: SnapshotUnit;
  /** Shown as the `title` tooltip when `current` is null. */
  missingReason?: string;
};

export type SnapshotKpi = SnapshotMetric & {
  id: string;
  label: string;
  /** Overrides the emitted unit suffix (weeks vs months of supply). */
  unitLabel?: string;
  note: string;
  linkPath: string | null;
  linkLabel: string;
  /** Which delta direction is healthy; "none" renders a neutral chip. */
  good: "up" | "down" | "none";
};

export type SnapshotRunInfo = {
  displayId: string | null;
  name: string | null;
  status: RunStatus;
  /** Formatted local timestamp of when the run finished (updatedAt). */
  finishedAt: string;
  /** The data-as-of date — the most important figure in the header. */
  asOf: string;
  settingsName: string;
  /** null → no predecessor: values render without delta chips. */
  prevDisplayId: string | null;
};

export type SnapshotTransitLane = {
  units: number | null;
  eta: string | null;
};

export type SnapshotLocation = {
  id: string;
  name: string;
  short: string;
  role: "hub" | "spoke" | "dc";
  units: number | null;
  value: number | null;
  wos: number | null;
  targetWos: number | null;
  inStock: number | null;
  skus: number | null;
  atRisk: number | null;
  /** Units leaving for customers per period (week or month per the toggle). */
  weeklyOut: number | null;
  weeklyOutPrev: number | null;
  /** This location's outflow split by channel, descending. */
  channelMix: SnapshotChannelMixEntry[] | null;
  /** Displayed supply figures follow the period; `wos`/`targetWos` stay
   *  weekly so health color is computed from one consistent ratio. */
  displayWos: number | null;
  displayTargetWos: number | null;
  /** Hub→spoke lane in transit to this location (hub-and-spoke only). */
  inTransit: SnapshotTransitLane | null;
  /** Vendor PO units inbound to this location (flat only). */
  inbound: number | null;
  nextArrival: string | null;
};

export type SnapshotSales = {
  weeklyUnits: number | null;
  weeklyUnitsPrev: number | null;
  weeklyRevenue: number | null;
  weeklyRevenuePrev: number | null;
  /** Outflow as a per-WEEK rate, whatever period is selected. The displayed
   *  month figure is a to-date total over however many weeks have elapsed, so
   *  anything dividing by it (network cover, turn rate) must use this instead
   *  — otherwise the ratio drifts with the day of the month rather than with
   *  the business. Same role as the weekly ratio behind the health colors. */
  weeklyRateUnits: number | null;
  /** Share of on-hand network inventory consumed per period. Derived from
   *  the rows on screen (Σ outflow ÷ Σ stock), never stored, so it always
   *  reconciles with the per-location figures. Can exceed 1 in month mode. */
  turnRate: number | null;
  turnRatePrev: number | null;
};

export type SnapshotInbound = {
  units: number | null;
  value: number | null;
  shipments: number | null;
  nextArrival: string | null;
};

export type SnapshotTransferLane = {
  fromHubId: string;
  toLocationId: string;
  /** Open transfer units arriving over the near horizon. */
  units: number;
  eta: string | null;
};

export type SnapshotRebalanceCard = {
  toLocationId: string;
  toLocationName: string;
  units: number | null;
  skus: number | null;
  value: number | null;
};

export type SnapshotRiskRow = {
  sku: string;
  description: string | null;
  location: string;
  role: "hub" | "spoke" | "dc" | null;
  /** Month-forward forecast split by channel for this SKU at this location;
   *  null when unavailable. Suppressed in the UI for single-channel customers,
   *  where the split carries no information. */
  channelForecast: { channel: string; units: number }[] | null;
};

export type SnapshotStockOutRow = SnapshotRiskRow & {
  /** Weeks already out of stock; null when computed from point-in-time data. */
  weeksOut: number | null;
  recentDemand: number;
  /** null when no price source exists for this run. */
  revenueAtRisk: number | null;
  /** Units of this SKU available at the hub; null → column not applicable. */
  hubCanCover: number | null;
};

export type SnapshotExcessRow = SnapshotRiskRow & {
  quantity: number;
  /** null when no cost source exists for this run. */
  value: number | null;
  monthsOfSupply: number;
  monthlyDemand: number;
};

export type SnapshotLowStockRow = SnapshotRiskRow & {
  quantity: number;
  /** Units needed to hit the target cover; null when demand is zero. */
  threshold: number | null;
  daysUntilStockout: number;
};

export type SnapshotTrailingMetric = SnapshotMetric & {
  id: string;
  label: string;
};

export type SnapshotViewModel = {
  isHubAndSpoke: boolean;
  period: SnapshotPeriod;
  /** Top channels by units for the period, with the remainder rolled up. */
  channels: SnapshotChannel[];
  run: SnapshotRunInfo;
  kpis: SnapshotKpi[];
  /** 13-week in-stock-rate series for the rail sparkline. */
  inStockTrend: number[];
  locations: SnapshotLocation[];
  sales: SnapshotSales;
  inbound: SnapshotInbound;
  /** Hub-and-spoke only; empty for flat customers. */
  rebalance: SnapshotRebalanceCard[];
  /** Open hub→spoke transfers per lane; null while unloaded. */
  transferLanes: SnapshotTransferLane[] | null;
  trailing: SnapshotTrailingMetric[];
  stockOuts: SnapshotStockOutRow[];
  excess: SnapshotExcessRow[];
  lowStock: SnapshotLowStockRow[];
  /** Deep-link paths, resolved per customer's flags. */
  links: {
    inventory: string;
    inventoryHealth: string;
    supply: string;
    transferPlanning: string;
  };
};

/** WOS-vs-target health used for node rings and left bars. */
export const wosHealth = (
  wos: number | null,
  target: number | null
): "under" | "light" | "heavy" | "on" | "unknown" => {
  if (wos == null || target == null || target === 0) {
    return "unknown";
  }
  const r = wos / target;
  if (r < 0.8) {
    return "under";
  }
  if (r < 0.95) {
    return "light";
  }
  if (r > 1.6) {
    return "heavy";
  }
  return "on";
};
